/**
 * IncumbentPreview — 区块 e：中间解快照预览
 *
 * 设计约束（§5.1 / F6 工单，方案 A 轻量聚合）：
 * - WxbGauge 覆盖率进度环（fill_rate，0-100 百分数）
 * - WxbBadge 空缺数徽章（vacant_positions）
 * - scheduled_shifts 数值卡
 * - 非员工×日期热力图（preview 只发聚合指标，无矩阵数据）
 * - incumbent.preview 缺失时显示空态（§3.7 降级铁律）
 * - 颜色全 var(--wx-*) CSS 变量
 * - 无 emoji 图标
 */

import React from 'react';
import { WxbGauge, WxbBadge } from '../../wxb-ui';
import type { PreviewSnapshot } from './monitorTypes';

// ── Props ─────────────────────────────────────────────────────────────────────

export interface IncumbentPreviewProps {
  /** 最新中间解快照（来自 latestPreview，可为 null） */
  preview: PreviewSnapshot | null;
  /** 当前已到第几次改进（solution_count） */
  solutionCount?: number;
  className?: string;
}

// ── 组件 ──────────────────────────────────────────────────────────────────────

const IncumbentPreview: React.FC<IncumbentPreviewProps> = ({
  preview,
  solutionCount,
  className = '',
}) => {
  // 空态降级（§3.7）
  if (!preview) {
    return (
      <div className={`incumbent-preview-empty ${className}`}>
        {/* 简单空态图示 */}
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
          <circle cx="16" cy="16" r="13" stroke="var(--wx-surface-3)" strokeWidth="4" fill="none" />
          <circle cx="16" cy="16" r="13" stroke="var(--wx-fg-4)" strokeWidth="4"
            fill="none" strokeDasharray="10 72" />
        </svg>
        暂无快照
      </div>
    );
  }

  const fillPercent = Math.max(0, Math.min(100, Math.round(preview.fill_rate)));
  const vacant = preview.vacant_positions;
  const scheduled = preview.scheduled_shifts;

  // 根据覆盖率选色：≥95% 绿，80-95% 蓝，<80% 琥珀
  const gaugeColor =
    fillPercent >= 95
      ? 'var(--wx-green-500)'
      : fillPercent >= 80
      ? 'var(--wx-blue-700)'
      : 'var(--wx-amber-500)';

  const vacantStatus: 'success' | 'warning' | 'error' =
    vacant === 0 ? 'success' : vacant <= 5 ? 'warning' : 'error';

  return (
    <div className={`incumbent-preview ${className}`}>
      {/* 第 N 次改进提示 */}
      {solutionCount != null && solutionCount > 0 && (
        <div className="incumbent-preview-solution-count">
          第 {solutionCount} 次改进
        </div>
      )}

      {/* 覆盖率进度环 */}
      <WxbGauge
        percent={fillPercent}
        size={120}
        title="岗位覆盖率"
        color={gaugeColor}
        label={`${fillPercent}%`}
      />

      {/* 空缺数 + 排班班次 */}
      <div className="incumbent-preview-metrics">
        <div className="incumbent-preview-metric">
          <WxbBadge
            label={`空缺 ${vacant} 岗位`}
            status={vacantStatus}
            variant="bar"
          />
        </div>

        <div className="incumbent-preview-metric">
          <span className="incumbent-preview-scheduled-value">
            {scheduled.toLocaleString()}
          </span>
          <span className="incumbent-preview-scheduled-label">已排班次</span>
        </div>
      </div>
    </div>
  );
};

export default React.memo(IncumbentPreview, (prev, next) => {
  return (
    prev.preview === next.preview &&
    prev.solutionCount === next.solutionCount &&
    prev.className === next.className
  );
});
